import React, { useEffect, useState } from 'react';
import { billingService } from '../clients/ApiClients';
import { Button } from '../common/Catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '../common/Catalyst/dialog';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from '../common/Catalyst/fieldset';
import { Input } from '../common/Catalyst/input';
import { Text } from '../common/Catalyst/text';
import { FieldPicker } from '../common/Custom/FieldPicker';
import ShowAlert from '../ShowAlert';
import {
  billingErrorMessage,
  formatMoney,
  newIdempotencyKey,
} from '../utils/billingHelpers';
import { TextSetting } from './BillingPolicyFields';
import { ledgerEntryType } from './enumLabels';

export type PostKind = 'charge' | 'credit';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onPosted: () => void;
  homeId: string;
  kind: PostKind;
}

const MONEY = /^\d+(\.\d{1,2})?$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Post a charge, or a credit, by hand.
 *
 * **Amount is always positive here.** The sign is the entry type's job:
 * a charge increases what the homeowner owes, a credit decreases it,
 * and asking somebody to type a minus sign is asking for the wrong one.
 *
 * **The idempotency key is minted when the dialog opens, not on
 * submit.** It lands in `ledger_entries.source_id`, which has a unique
 * index, so a double-click collides and posts once. Minting it per
 * submit would defeat the whole point.
 *
 * The type list comes from the entry types flagged manually postable —
 * `assessment` is a billing run's job and `payment` is a real-world
 * event with a check number, so neither is offered rather than being
 * offered and 400ing.
 */
const PostEntryDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  onPosted,
  homeId,
  kind,
}) => {
  const options = ledgerEntryType.manual(kind);
  const [entryType, setEntryType] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [key, setKey] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setEntryType(options[0]?.value ?? '');
    setAmount('');
    setDescription('');
    setEffectiveDate('');
    setDueDate('');
    setErrors({});
    setFailure(null);
    // One key per dialog open — see the note above.
    setKey(newIdempotencyKey());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, kind]);

  const submit = async (): Promise<void> => {
    const found: Record<string, string> = {};
    if (!MONEY.test(amount.trim())) {
      found.amount = 'Enter a positive amount like 75 or 75.00.';
    }
    if (!description.trim()) {
      found.description =
        'Say what this is for — it appears on the homeowner’s statement.';
    }
    if (effectiveDate && !DATE.test(effectiveDate)) {
      found.effectiveDate = 'Use a date, or leave blank for today.';
    }
    if (dueDate && !DATE.test(dueDate)) {
      found.dueDate = 'Use a date, or leave it blank.';
    }
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    try {
      setSaving(true);
      setFailure(null);
      if (kind === 'charge') {
        await billingService.postCharge({
          homeId,
          chargeCreate: {
            entry_type: entryType,
            amount: amount.trim(),
            description: description.trim(),
            effective_date: effectiveDate || null,
            due_date: dueDate || null,
            idempotency_key: key,
          },
        });
      } else {
        await billingService.postCredit({
          homeId,
          creditCreate: {
            entry_type: entryType,
            amount: amount.trim(),
            description: description.trim(),
            effective_date: effectiveDate || null,
            idempotency_key: key,
          },
        });
      }
      onPosted();
      onClose();
    } catch (err) {
      console.error('Error posting entry:', err);
      setFailure(billingErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const chosen = options.find((o) => o.value === entryType);

  return (
    <Dialog open={isOpen} onClose={onClose} size="xl">
      <DialogTitle>
        {kind === 'charge' ? 'Post a charge' : 'Post a credit'}
      </DialogTitle>
      <DialogBody>
        {failure && (
          <div className="mb-4">
            <ShowAlert
              alertType="error"
              alertTitle={
                kind === 'charge'
                  ? 'The charge was not posted'
                  : 'The credit was not posted'
              }
              alertMessage={failure}
              isVisible={!!failure}
              onClose={() => setFailure(null)}
              timeout={14000}
              isInline
            />
          </div>
        )}

        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Type</Label>
              <FieldPicker
                label="Type"
                value={entryType}
                onChange={setEntryType}
                options={options.map((o) => ({
                  value: o.value,
                  label: o.label,
                }))}
              />
              {chosen?.help && (
                <Description className="mt-1">{chosen.help}</Description>
              )}
            </Field>

            <TextSetting
              label="Amount"
              help={
                kind === 'charge'
                  ? 'A positive amount. This increases what the homeowner owes.'
                  : 'A positive amount. This reduces what the homeowner owes.'
              }
              prefix="$"
              value={amount}
              error={errors.amount}
              onChange={setAmount}
            />

            <Field>
              <Label>Description</Label>
              <Description>
                This is what the homeowner reads on their statement, so write it
                for them.
              </Description>
              <Input
                value={description}
                invalid={!!errors.description}
                onChange={(e) => setDescription(e.target.value)}
              />
              {errors.description && (
                <ErrorMessage>{errors.description}</ErrorMessage>
              )}
            </Field>

            <Field>
              <Label>Effective date</Label>
              <Description>
                The date it counts from. Blank means today, on the
                association&rsquo;s calendar.
              </Description>
              <Input
                type="date"
                value={effectiveDate}
                invalid={!!errors.effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
                className="max-w-[14rem]"
              />
              {errors.effectiveDate && (
                <ErrorMessage>{errors.effectiveDate}</ErrorMessage>
              )}
            </Field>

            {kind === 'charge' && (
              <Field>
                <Label>Due date</Label>
                <Description>
                  Drives aging and the grace period. Leave blank for a charge
                  that is not chased.
                </Description>
                <Input
                  type="date"
                  value={dueDate}
                  invalid={!!errors.dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="max-w-[14rem]"
                />
                {errors.dueDate && (
                  <ErrorMessage>{errors.dueDate}</ErrorMessage>
                )}
              </Field>
            )}

            {MONEY.test(amount.trim()) && (
              <Text>
                This writes one ledger entry
                {kind === 'charge'
                  ? ` increasing what this home owes by ${formatMoney(
                      amount.trim()
                    )}`
                  : ` reducing what this home owes by ${formatMoney(
                      amount.trim()
                    )}`}
                . Nothing is edited — undoing it later means posting a reversal
                against it.
              </Text>
            )}
          </FieldGroup>
        </Fieldset>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button color="sky" onClick={submit} disabled={saving}>
          {saving
            ? 'Posting…'
            : kind === 'charge'
              ? 'Post charge'
              : 'Post credit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PostEntryDialog;
